import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';
import { readListColumnPrefs, writeListColumnPrefs } from '@shared/lib/listColumnPreferences';

/**
 * Per-org list column visibility with localStorage persistence.
 * Only columns listed in `catalog` are stored; select/actions columns stay outside the catalog.
 */
export function useListColumnVisibility(
  entityKey: string,
  orgId: string | null,
  catalog: ListColumnCatalogItem[],
) {
  const catalogKey = useMemo(() => catalog.map((c) => c.id).join('|'), [catalog]);

  const [visible, setVisible] = useState<Record<string, boolean>>(() =>
    readListColumnPrefs(entityKey, orgId, catalog),
  );

  useEffect(() => {
    setVisible(readListColumnPrefs(entityKey, orgId, catalog));
  }, [entityKey, orgId, catalogKey]);

  const show = useCallback(
    (id: string) => visible[id] === true,
    [visible],
  );

  const setColumn = useCallback(
    (id: string, value: boolean) => {
      setVisible((prev) => {
        const next = { ...prev, [id]: value };
        if (orgId) writeListColumnPrefs(entityKey, orgId, next);
        return next;
      });
    },
    [entityKey, orgId],
  );

  const reset = useCallback(() => {
    const next = readListColumnPrefs(entityKey, null, catalog);
    setVisible(next);
    if (orgId) writeListColumnPrefs(entityKey, orgId, next);
  }, [entityKey, orgId, catalog, catalogKey]);

  return { visible, show, setColumn, reset, catalog };
}
